import React, { useEffect, useState } from 'react'
import styled from 'styled-components'
import Tippy from '@tippyjs/react'
import { theme } from 'infra/theme'
import { Column, ResponsiveLayout, Row } from 'components/common/layout'
import {
  BaseText,
  BoldText,
  DescText,
  H2Text,
  H3Text,
  H4Text,
  H5Text,
  SemiBoldText,
  SmallText,
} from 'components/common/text'
import { BtnType, Button } from 'components/common/button'
import { TextInput } from 'components/common/input'
import { Card } from 'components/common/card'
import { MyLink } from 'components/common/link'
import { useIsDesktop } from 'utils/hooks'
import { ResponsiveDisplay } from 'components/common/layout'
import { useRouter } from 'next/router'
import { analytics } from 'infra/analytics'
import { FiChevronRight, FiMinus } from 'react-icons/fi'
import { CREDIT_CHARGE_URL } from 'infra/constants'

const SALARY = [
  { title: '2,800 미만', percentage: 4, value: { min: 0, max: 2799 } },
  {
    title: '2,800 이상 4,000 미만',
    percentage: 5,
    value: { min: 2800, max: 3999 },
  },
  {
    title: '4,000 이상',
    percentage: 6,
    value: { min: 4000, max: 10000000 },
  },
]

const TOTAL_HIRE = [
  { title: '1명', percentage: 5, value: 1 },
  { title: '2명', percentage: 10, value: 2 },
  { title: '3명', percentage: 15, value: 3 },
  { title: '4명', percentage: 20, value: 4 },
  { title: '5명~', percentage: 25, value: 5 },
]

const ContentAccordion: React.FC<{
  title: string
  description: string
  isFullWidth?: boolean
}> = ({ title, description, isFullWidth = false }) => {
  const [isOpen, setIsOpen] = useState(false)
  return (
    <Container
      onClick={() => setIsOpen((prev) => !prev)}
      isOpen={isOpen}
      isFullWidth={isFullWidth}
    >
      <Column style={{ flex: 1, gap: 16, textAlign: 'start' }}>
        <AccordionSubText>{title}</AccordionSubText>
        {isOpen && (
          <DescText dangerouslySetInnerHTML={{ __html: description }} />
        )}
      </Column>
      <div style={{ position: 'relative' }}>
        <FiMinus
          size={24}
          color={theme.color.primary}
          style={{
            transition: '0.4s',
            position: 'absolute',
            transform: isOpen ? 'rotate(0deg)' : 'rotate(90deg)',
          }}
        />
        <FiMinus size={24} color={theme.color.primary} />
      </div>
    </Container>
  )
}

export const Credit: React.FC = () => {
  return (
    <Column style={{ alignItems: 'center' }}>
      <BoldText
        style={{
          fontSize: 40,
          textAlign: 'center',
          lineHeight: 1.5,
          marginBottom: 24,
        }}
      >
        {'💳 선불형 크레딧,\n더 많은 혜택을 경험하세요!'}
      </BoldText>
      <SubText style={{ textAlign: 'center' }}>
        현금처럼 사용할 수 있는 크레딧을 지금 구매하시면{'\n'}
        <b>최대 100만원!</b> 추가 금액 혜택을 드려요
      </SubText>
      <Column
        style={{
          borderRadius: 40,
          border: `1px solid ${theme.color.line}`,
          overflow: 'hidden',
          margin: '60px 0',
        }}
      >
        <TableGrid
          style={{
            padding: '16px 0',
            borderBottom: `1px solid ${theme.color.gray2}`,
          }}
        >
          <Column style={{ alignItems: 'center' }}>
            <BoldText style={{ fontSize: theme.fontSize.h5 }}>
              상품 가격
            </BoldText>
            <DescText>(만원)</DescText>
          </Column>
          <div />
          <Column style={{ alignItems: 'center' }}>
            <BoldText style={{ fontSize: theme.fontSize.h5 }}>혜택</BoldText>
            <DescText>(만원)</DescText>
          </Column>
          <div />
          <Column style={{ alignItems: 'center' }}>
            <BoldText style={{ fontSize: theme.fontSize.h5 }}>
              지급 크레딧
            </BoldText>
            <DescText>(만원)</DescText>
          </Column>
        </TableGrid>
        <TableGrid
          style={{
            padding: '24px 0',
            backgroundColor: '#E9FBF5',
            borderBottom: `1px dashed ${theme.color.gray2}`,
          }}
        >
          <H4Text
            style={{ fontWeight: theme.fontWeight.bold, color: '#00513A' }}
          >
            100
          </H4Text>
          <H4Text>+</H4Text>
          <H4Text
            style={{ fontWeight: theme.fontWeight.bold, color: '#00513A' }}
          >
            5
          </H4Text>
          <FiChevronRight size={22} />
          <Row style={{ gap: 8 }}>
            <H4Text
              style={{
                fontWeight: theme.fontWeight.bold,
                color: '#00513A',
              }}
            >
              105
            </H4Text>
            <DescText style={{ color: '#00513A' }}>+5%</DescText>
          </Row>
        </TableGrid>
        <TableGrid
          style={{
            padding: '40px 0',
            backgroundColor: '#D3F7EC',
            borderBottom: `1px dashed ${theme.color.gray2}`,
          }}
        >
          <H3Text
            style={{
              fontWeight: theme.fontWeight.bold,
              color: theme.color.primary8,
            }}
          >
            300
          </H3Text>
          <H3Text>+</H3Text>
          <H3Text
            style={{
              fontWeight: theme.fontWeight.bold,
              color: theme.color.primary8,
            }}
          >
            30
          </H3Text>
          <FiChevronRight size={32} />
          <Row style={{ gap: 8 }}>
            <H3Text
              style={{
                fontWeight: theme.fontWeight.bold,
                color: theme.color.primary8,
              }}
            >
              330
            </H3Text>
            <H4Text style={{ color: theme.color.primary8 }}>+10%</H4Text>
          </Row>
        </TableGrid>
        <TableGrid style={{ padding: '48px 0', backgroundColor: '#BDF2E2' }}>
          <BoldText style={{ fontSize: theme.fontSize.h1 }}>500</BoldText>
          <H2Text style={{ fontWeight: theme.fontWeight.bold }}>+</H2Text>
          <BoldText style={{ fontSize: theme.fontSize.h1 }}>100</BoldText>
          <FiChevronRight size={44} />
          <Row style={{ gap: 8 }}>
            <BoldText style={{ fontSize: theme.fontSize.h1 }}>600</BoldText>
            <H3Text>+20%</H3Text>
          </Row>
        </TableGrid>
      </Column>
      <BoldText
        style={{
          textAlign: 'center',
          color: theme.color.black2,
          lineHeight: 1.5,
        }}
      >
        {
          '※ 채용 수수료 정산은 크레딧 또는 후불형 한 가지 방식으로만 가능합니다.\n잔여 크레딧이 부족하면, 추가 충전 후 이용해주세요!'
        }
      </BoldText>
    </Column>
  )
}

export const PricingLanding: React.FC = () => {
  const router = useRouter()
  const isDashboard = router.pathname.includes('dashboard')
  const isDesktop = useIsDesktop()
  const [salary, setSalary] = useState(0)
  const [totalHire, setTotalHire] = useState(0)
  const salaryPercentage = salary < 2800 ? 4.4 : salary < 4000 ? 5.5 : 6.6
  const totalHirePercentage = totalHire >= 5 ? 25 : totalHire * 5
  const baseFee = salary * 10000 * (7.7 / 100)
  const salaryFee = salary * 10000 * (salaryPercentage / 100)
  const salaryDisCountedFee = Math.floor(baseFee - salaryFee)
  const totalHireFee =
    totalHire > 0 ? Math.floor(salaryFee * (totalHirePercentage / 100)) : 0
  const totalFee = Math.floor(
    salary * 10000 * (salaryPercentage / 100) - totalHireFee,
  )
  const commaFormat = (value: number) => {
    return value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')
  }

  useEffect(() => {
    analytics.traffic('startup_service_pricing')
  }, [])
  return (
    <>
      <Section style={{ backgroundColor: theme.color.white1 }}>
        <ResponsiveLayout>
          <Column style={{ gap: isDesktop ? 24 : 0 }}>
            <HeaderText>{`스타트업에게\n더욱 합리적인 채용 수수료`}</HeaderText>
            <ResponsiveRow
              style={{
                justifyContent: 'space-between',
                gap: isDesktop ? 0 : 40,
              }}
            >
              <SubText>
                <ResponsiveDisplay isDisableMobile>
                  {`모든 채용에서 같은 수수료를 적용하는게 부담스러웠나요?`}
                </ResponsiveDisplay>
                이제는{' '}
                {
                  <span style={{ color: theme.color.primary }}>
                    연봉 구간별로 수수료를 할인
                  </span>
                }{' '}
                받아보세요!
              </SubText>
              <Column
                style={{
                  gap: 8,
                  alignItems: 'center',
                  flexDirection: isDesktop ? 'column' : 'column-reverse',
                  width: isDesktop ? undefined : '100%',
                }}
              >
                <MyLink newTab href='https://tally.so/r/mJz12d'>
                  <Button
                    variant={BtnType.LINEAR}
                    style={{
                      width: isDesktop ? 380 : '100%',
                      padding: isDesktop ? 24 : 16,
                      fontSize: 20,
                    }}
                  >
                    ✋ 채용사실 알리고 할인 받기
                  </Button>
                </MyLink>
              </Column>
            </ResponsiveRow>
          </Column>
        </ResponsiveLayout>
      </Section>
      <ResponsiveDisplay isDisableMobile>
        <Column
          style={{
            background: theme.color.linear,
            alignItems: 'center',
            padding: '40px 0',
          }}
        >
          <HeaderText style={{ color: theme.color.white1 }}>
            CREDIT 상품이 새로 출시되었어요!
          </HeaderText>
        </Column>
        <Section>
          <ResponsiveLayout>
            <Credit />
            <Row style={{ justifyContent: 'center', marginTop: 16 }}>
              <MyLink newTab href={CREDIT_CHARGE_URL}>
                <Button
                  variant={BtnType.LINEAR}
                  style={{ fontSize: 20, padding: 20, width: 380 }}
                >
                  💳 크레딧 충전하고 혜택 받기
                </Button>
              </MyLink>
            </Row>
          </ResponsiveLayout>
        </Section>
      </ResponsiveDisplay>
      <ResponsiveDisplay isDisableMobile>
        <Section style={{ backgroundColor: theme.color.background3 }}>
          <ResponsiveLayout>
            <Column style={{ gap: 100 }}>
              <Column style={{ gap: 8, alignItems: 'center' }}>
                <BoldText style={{ fontSize: 40 }}>
                  채용 수수료 알아보기
                </BoldText>
                <SubText>
                  정규직 계약에 대한 계산기이며, 계약 형태에 따라 실제 수수료는
                  달라질 수 있습니다.
                </SubText>
              </Column>
              <Column style={{ gap: 32 }}>
                <Row style={{ justifyContent: 'space-between' }}>
                  <Row style={{ gap: 32 }}>
                    <Column style={{ gap: 4 }}>
                      <BaseText>이번에 채용한</BaseText>
                      <BoldText style={{ fontSize: 22 }}>
                        인재의 연봉을 입력해주세요.
                      </BoldText>
                    </Column>
                    <Card
                      style={{
                        flexDirection: 'row',
                        height: 70,
                        width: 180,
                        padding: '0 16px',
                        justifyContent: 'end',
                        alignItems: 'center',
                        boxShadow: '4px 4px 8px 0px rgba(0, 0, 0, 0.08) inset',
                        gap: 8,
                      }}
                    >
                      <TextInput
                        style={{
                          border: 0,
                          flex: 1,
                          textAlign: 'end',
                          fontWeight: theme.fontWeight.bold,
                          fontSize: 20,
                        }}
                        type='number'
                        value={salary.toString().replace(/(^0+)/, '')}
                        onChange={(e) => setSalary(Number(e.target.value))}
                      />
                      <BoldText style={{ fontSize: 20 }}>만 원</BoldText>
                    </Card>
                  </Row>
                  <Row style={{ gap: 32 }}>
                    <Column style={{ gap: 4 }}>
                      <BaseText>2023년부터 그룹바이에서 채용한</BaseText>
                      <BoldText style={{ fontSize: 22 }}>
                        누적 채용 수를 입력해주세요.
                      </BoldText>
                    </Column>
                    <Card
                      style={{
                        flexDirection: 'row',
                        height: 70,
                        width: 180,
                        padding: '0 16px',
                        justifyContent: 'end',
                        alignItems: 'center',
                        boxShadow: '4px 4px 8px 0px rgba(0, 0, 0, 0.08) inset',
                        gap: 8,
                      }}
                    >
                      <TextInput
                        style={{
                          border: 0,
                          flex: 1,
                          textAlign: 'end',
                          fontWeight: theme.fontWeight.bold,
                          fontSize: 20,
                        }}
                        type='number'
                        value={totalHire.toString().replace(/(^0+)/, '')}
                        onChange={(e) => setTotalHire(Number(e.target.value))}
                      />
                      <BoldText style={{ fontSize: 20 }}>명</BoldText>
                    </Card>
                  </Row>
                </Row>
                <Card
                  style={{
                    padding: 40,
                    border: `1px solid ${theme.color.line}`,
                  }}
                >
                  <Row style={{ gap: 80, alignItems: 'end' }}>
                    <Column style={{ flex: 1.3, gap: 24 }}>
                      <Column style={{ gap: 10 }}>
                        <Row style={{ justifyContent: 'space-between' }}>
                          <Row style={{ gap: 8 }}>
                            <BoldText
                              style={{ fontSize: 14, color: theme.color.gray1 }}
                            >
                              연봉 구간별 수수료율
                            </BoldText>
                            <BaseText
                              style={{ fontSize: 12, color: theme.color.gray1 }}
                            >
                              (단위 : 만 원)
                            </BaseText>
                          </Row>
                          <SmallText
                            style={{ color: theme.color.gray1, fontSize: 12 }}
                          >
                            (부가세 별도)
                          </SmallText>
                        </Row>
                        {SALARY.map((item) => {
                          const isIncludeSalary =
                            salary !== 0 &&
                            salary >= item.value.min &&
                            salary <= item.value.max
                          return (
                            <Card
                              key={item.title}
                              style={{
                                padding: 24,
                                borderRadius: 8,
                                backgroundColor: isIncludeSalary
                                  ? theme.color.primary
                                  : theme.color.background,
                              }}
                            >
                              <Row style={{ justifyContent: 'space-between' }}>
                                <BoldText
                                  style={{
                                    fontSize: theme.fontSize.h5,
                                    color: isIncludeSalary
                                      ? theme.color.white1
                                      : theme.color.black2,
                                  }}
                                >
                                  {item.title}
                                </BoldText>
                                <BoldText
                                  style={{
                                    fontSize: theme.fontSize.h3,
                                    color: isIncludeSalary
                                      ? theme.color.white1
                                      : theme.color.black2,
                                  }}
                                >
                                  {item.percentage}%
                                </BoldText>
                              </Row>
                            </Card>
                          )
                        })}
                      </Column>
                      <Column style={{ gap: 10 }}>
                        <BoldText
                          style={{ fontSize: 14, color: theme.color.gray1 }}
                        >
                          누적 채용 추가 할인
                        </BoldText>
                        <HireWrapper>
                          {TOTAL_HIRE.map((item) => {
                            const isIncludeTotalHire =
                              totalHire === item.value ||
                              (item.value === 5 && totalHire > 5)
                            return (
                              <Column
                                key={item.title}
                                style={{
                                  width: 85,
                                  height: 75,
                                  backgroundColor: isIncludeTotalHire
                                    ? theme.color.primary
                                    : theme.color.background,
                                  alignItems: 'center',
                                  justifyContent: 'space-evenly',
                                }}
                              >
                                <BoldText
                                  style={{
                                    fontSize: 12,
                                    color: isIncludeTotalHire
                                      ? theme.color.white1
                                      : theme.color.black2,
                                  }}
                                >
                                  {item.title}
                                </BoldText>
                                <BoldText
                                  style={{
                                    fontSize: theme.fontSize.h5,
                                    color: isIncludeTotalHire
                                      ? theme.color.white1
                                      : theme.color.black1,
                                  }}
                                >
                                  {item.percentage}%
                                </BoldText>
                              </Column>
                            )
                          })}
                        </HireWrapper>
                      </Column>
                    </Column>
                    <Column style={{ flex: 1.7, justifyContent: 'flex-end' }}>
                      <Column style={{ gap: 60 }}>
                        <Column style={{ gap: 32 }}>
                          <Row style={{ justifyContent: 'space-between' }}>
                            <BaseText style={{ fontSize: theme.fontSize.h5 }}>
                              기본 채용 수수료
                              {
                                <span style={{ color: theme.color.gray2 }}>
                                  {' '}
                                  (채용 플랫폼 평균 7.7%)
                                </span>
                              }
                            </BaseText>
                            <BaseText
                              style={{
                                fontSize: theme.fontSize.h5,
                                color: theme.color.black2,
                                textDecoration: 'line-through',
                              }}
                            >
                              {commaFormat(baseFee)} 원
                            </BaseText>
                          </Row>
                          {salaryDisCountedFee > 0 && (
                            <Row style={{ justifyContent: 'space-between' }}>
                              <BaseText
                                style={{
                                  fontSize: theme.fontSize.h5,
                                  color: theme.color.primary,
                                  fontWeight: theme.fontWeight.bold,
                                }}
                              >
                                연봉 구간별 수수료율 적용
                              </BaseText>
                              <BaseText
                                style={{
                                  fontSize: theme.fontSize.h5,
                                  color: theme.color.primary,
                                  fontWeight: theme.fontWeight.bold,
                                }}
                              >
                                -{commaFormat(salaryDisCountedFee)} 원
                              </BaseText>
                            </Row>
                          )}
                          {totalHireFee > 0 && (
                            <Row style={{ justifyContent: 'space-between' }}>
                              <BaseText
                                style={{
                                  fontSize: theme.fontSize.h5,
                                  color: theme.color.primary,
                                  fontWeight: theme.fontWeight.bold,
                                }}
                              >
                                누적 채용 할인 적용
                              </BaseText>
                              <BaseText
                                style={{
                                  fontSize: theme.fontSize.h5,
                                  color: theme.color.primary,
                                  fontWeight: theme.fontWeight.bold,
                                }}
                              >
                                -{commaFormat(totalHireFee)} 원
                              </BaseText>
                            </Row>
                          )}
                          <hr />
                          <Column style={{ alignItems: 'end', gap: 5 }}>
                            <Row
                              style={{
                                justifyContent: 'space-between',
                                width: '100%',
                              }}
                            >
                              <Row style={{ gap: 16 }}>
                                <BoldText
                                  style={{ fontSize: theme.fontSize.h5 }}
                                >
                                  할인된 채용 수수료
                                </BoldText>
                                <Tippy
                                  content={
                                    <Row
                                      style={{
                                        backgroundColor: theme.color.white1,
                                        border: `1px solid ${theme.color.black1}`,
                                        padding: '14px 18px',
                                        borderRadius: 8,
                                      }}
                                    >
                                      <BaseText
                                        style={{
                                          fontSize: 12,
                                          lineHeight: '18px',
                                        }}
                                      >{`채용 후 1개월 이내 퇴사 시 수수료 100% 환불\n채용 후 3개월 이내 퇴사 시 수수료 75% 환불`}</BaseText>
                                    </Row>
                                  }
                                >
                                  <BaseText
                                    style={{
                                      cursor: 'default',
                                      color: theme.color.gray1,
                                      textDecoration: 'underline',
                                    }}
                                  >
                                    환불정책
                                  </BaseText>
                                </Tippy>
                              </Row>
                              <BoldText style={{ fontSize: theme.fontSize.h3 }}>
                                {commaFormat(totalFee)} 원
                              </BoldText>
                            </Row>
                            <SmallText
                              style={{
                                color: theme.color.gray1,
                                fontSize: 12,
                              }}
                            >
                              (부가세 포함)
                            </SmallText>
                          </Column>
                        </Column>
                        {isDashboard && (
                          <Row style={{ justifyContent: 'end' }}>
                            <MyLink newTab href='https://tally.so/r/mJz12d'>
                              <Button
                                style={{
                                  width: 'fit-content',
                                  fontSize: theme.fontSize.h5,
                                  padding: '16px 32px',
                                }}
                              >
                                채용사실 알리고 할인 받기
                              </Button>
                            </MyLink>
                          </Row>
                        )}
                      </Column>
                    </Column>
                  </Row>
                </Card>
              </Column>
            </Column>
          </ResponsiveLayout>
        </Section>
      </ResponsiveDisplay>
      {isDashboard && (
        <ResponsiveDisplay isDisableMobile>
          <MyLink href='https://tally.so/r/mJz12d' newTab>
            <Section
              style={{
                backgroundColor: theme.color.white1,
                textAlign: 'center',
                cursor: 'pointer',
              }}
            >
              <ResponsiveLayout>
                <BoldText style={{ fontSize: 50 }}>
                  채용 수수료,{' '}
                  {
                    <span style={{ color: theme.color.primary }}>
                      먼저 알리고
                    </span>
                  }{' '}
                  할인 받으세요!
                </BoldText>
              </ResponsiveLayout>
            </Section>
          </MyLink>
        </ResponsiveDisplay>
      )}
      <Section
        style={{
          backgroundColor:
            !isDashboard && isDesktop
              ? theme.color.white1
              : theme.color.background3,
        }}
      >
        <ResponsiveLayout>
          <Column style={{ gap: 32, alignItems: 'center' }}>
            <BoldText style={{ fontSize: isDesktop ? theme.fontSize.h1 : 20 }}>
              연봉 구간에 따라 달라지는 수수료율
            </BoldText>
            <Column style={{ gap: 16 }}>
              <Table>
                <TableRow>
                  <TableItem style={{ gap: 16 }}>
                    <TableHeaderText>후보자 연봉</TableHeaderText>
                    <ResponsiveDisplay isDisableMobile>
                      <H5Text>(단위 : 만 원)</H5Text>
                    </ResponsiveDisplay>
                  </TableItem>
                  <TableItem>
                    <TableHeaderText>채용 수수료</TableHeaderText>
                  </TableItem>
                </TableRow>
                {SALARY.map((item, index) => (
                  <TableRow key={index}>
                    <TableItem style={{ gap: 16 }}>
                      <TableRowText>{item.title}</TableRowText>
                    </TableItem>
                    <TableItem>
                      <TableRowText>연봉의 {item.percentage}%</TableRowText>
                    </TableItem>
                  </TableRow>
                ))}
                <TableRow>
                  <TableItem style={{ gap: 16 }}>
                    <TableRowText
                      style={{
                        color: theme.color.alert,
                        fontSize: isDesktop ? 25 : 13,
                      }}
                    >
                      먼저 알리지 않은 경우
                    </TableRowText>
                  </TableItem>
                  <TableItem>
                    <TableRowText
                      style={{
                        color: theme.color.alert,
                        fontSize: isDesktop ? 25 : 13,
                      }}
                    >
                      연봉의 7%
                    </TableRowText>
                  </TableItem>
                </TableRow>
              </Table>
              <DescText
                style={{
                  fontSize: isDesktop ? undefined : 12,
                  lineHeight: isDesktop ? undefined : '18px',
                  textAlign: 'center',
                }}
              >
                {
                  <span style={{ color: theme.color.alert }}>
                    근로 시작 후 2주가 지났음에도 먼저 알리지 않은 채용 건
                  </span>
                }
                을 발견 시 연봉 구간과 상관없이 7% 수수료가 부과됩니다.
              </DescText>
            </Column>
          </Column>
        </ResponsiveLayout>
      </Section>
      {isDashboard && (
        <Section style={{ backgroundColor: theme.color.white1 }}>
          <ResponsiveLayout>
            <ResponsiveRow
              style={{
                justifyContent: 'space-between',
                gap: isDesktop ? undefined : 8,
              }}
            >
              <HeaderText>{`혹시, 아직 알리지 않은\n채용 건이 있나요?`}</HeaderText>
              <Column
                style={{
                  gap: isDesktop ? 16 : 40,
                  alignItems: isDesktop ? 'center' : 'flex-start',
                  width: isDesktop ? undefined : '100%',
                }}
              >
                <BoldText
                  style={{
                    fontWeight: isDesktop
                      ? undefined
                      : theme.fontWeight.regular,
                  }}
                >
                  지금 알려주시면 할인된 수수료가 적용됩니다!
                </BoldText>
                <MyLink newTab href='https://tally.so/r/mJz12d'>
                  <Button
                    variant={BtnType.LINEAR}
                    style={{
                      fontSize: 20,
                      padding: isDesktop ? 20 : 16,
                      width: isDesktop ? 380 : undefined,
                    }}
                  >
                    ✋ 채용사실 알리고 할인 받기
                  </Button>
                </MyLink>
              </Column>
            </ResponsiveRow>
          </ResponsiveLayout>
        </Section>
      )}
      <Section
        style={{
          backgroundColor:
            (!isDashboard && isDesktop) || isDashboard
              ? theme.color.background3
              : theme.color.white1,
        }}
      >
        <ResponsiveLayout>
          <Column
            style={{ alignItems: 'center', textAlign: 'center', gap: 60 }}
          >
            <Column style={{ gap: 16 }}>
              <BoldText
                style={{
                  fontSize: isDesktop ? 35 : 20,
                  lineHeight: isDesktop ? '50px' : '32px',
                }}
              >
                {`2024년에는?\n누적 채용에 따른 추가 할인`}
              </BoldText>
              <DescText
                style={{
                  fontSize: isDesktop ? undefined : 13,
                  lineHeight: isDesktop ? undefined : '18px',
                }}
              >
                누적 채용 수는 2023년부터 채용 후 3개월 이상 근로가 확인된
                정규직 수 기준으로 계산됩니다.
              </DescText>
            </Column>
            <Column style={{ gap: 16 }}>
              <Table>
                <TableRow>
                  <TableItem>
                    <TableHeaderText>누적 채용수</TableHeaderText>
                  </TableItem>
                  <TableItem>
                    <TableHeaderText>다음 채용 할인율</TableHeaderText>
                  </TableItem>
                </TableRow>
                {TOTAL_HIRE.map((item, index) => {
                  const fontSize = isDesktop ? 20 + index + index : 13 + index
                  const backgroundColor = `rgba(36, 213, 158, ${
                    ((index + 1) * 5) / 100
                  })`
                  return (
                    <TableRow key={index} style={{ backgroundColor }}>
                      <TableItem>
                        <TableRowText style={{ fontSize }}>
                          {item.title}
                        </TableRowText>
                      </TableItem>
                      <TableItem>
                        <TableRowText style={{ fontSize }}>
                          -{item.percentage}%
                        </TableRowText>
                      </TableItem>
                    </TableRow>
                  )
                })}
              </Table>
              <DescText
                style={{
                  fontSize: isDesktop ? undefined : 13,
                  lineHeight: isDesktop ? undefined : '18px',
                }}
              >
                {`* 해당 할인은 이벤트성으로 계약서에 명시되어있지 않으며, 수수료 책정 시 그룹바이 담당자가 계산해드립니다.\n"선불형 크레딧"을 이용하는 기업은 누적 채용에 따른 추가 할인이 적용되지 않습니다"`}
              </DescText>
            </Column>
          </Column>
        </ResponsiveLayout>
      </Section>
      <Section
        style={{
          backgroundColor:
            isDesktop || !isDashboard
              ? theme.color.background3
              : theme.color.white1,
        }}
      >
        <ResponsiveLayout>
          <Column style={{ textAlign: 'center', gap: 60 }}>
            <BoldText style={{ fontSize: isDesktop ? 56 : 20 }}>FAQs</BoldText>
            <DropdownSectionWrapper>
              <ContentAccordion
                isFullWidth
                title='계약직 채용 시 수수료는 어떻게 되나요?'
                description='후보자와 계약의 종료시점이 명시된 근로계약을 체결했을 경우, 채용 수수료 = (계약기간(최대 1년) 동안 지급되는 총 급여) x (연봉에 따른 수수료율)로 계산됩니다. 만약 3개월 이내의 계약기간 종료 후 정규직 등으로 근로계약을 갱신했을 경우, 추가 수수료가 발생할 수 있습니다.'
              />
              <ContentAccordion
                isFullWidth
                title='대학생 인턴 채용 시 수수료는 어떻게 되나요?'
                description='수수료 책정 시 계약 기간과 연봉을 기준으로 계산되기에 대학생 인턴은 보통 계약직 채용으로 분류됩니다. 수수료는 위 계약직 채용 시 수수료 공식에 따라 계산됩니다.'
              />
              <ContentAccordion
                isFullWidth
                title='도급(프리랜서) 계약 시 수수료는 어떻게 되나요?'
                description='후보자와 도급 혹은 용역계약을 체결한 경우, 채용 수수료는 계약서에 명시된 총 계약금의 6%로 계산됩니다.'
              />
              <ContentAccordion
                isFullWidth
                title='환불 규정이 어떻게 되나요?'
                description='정규직 채용의 경우 1개월 이내 퇴사 시 수수료의 100%를, 1~3개월이내 퇴사시 수수료의 75%를 환불해드리고 있습니다. 계약직 채용의 경우 6개월 이내 계약일 경우 계약기간의 50% 이내 퇴사시 75% 환불, 계약기간의 25%를 채우지 못하면 100% 환불, 6개월 이후 계약직 채용은 3개월, 1개월 이내 퇴사시 각각 75%, 100% 환불이 진행됩니다.'
              />
              <ContentAccordion
                isFullWidth
                title='채용 수수료 지급 시점 및 프로세스가 어떻게 되나요?'
                description='기업은 채용이 확정되면 근로 개시일 기준 2주 이내로 그룹바이에 채용확정을 고지해야합니다. 이때 그룹바이는 근로계약서 기준으로 수수료 계산 및 세금계산서를 발급해드리고, 기업은 근로 개시일 기준 1달 이내로 채용 수수료를 지급해야합니다.'
              />
              <ContentAccordion
                isFullWidth
                title='2023년에 결정된 채용도 4~6% 수수료율이 적용되나요?'
                description='변경된 채용 수수료는 구직자와의 최초 상호작용(서류 합격, 스카우트 제안) 시점이 2024년 01월 01일 이후인 채용 건에 대해서만 적용됩니다. 따라서 기존 기업의 경우 새로 계약서를 서명하셔도 일부 채용 건은 기존대로  6%의 수수료가 적용될 수 있습니다.'
              />
            </DropdownSectionWrapper>
          </Column>
        </ResponsiveLayout>
      </Section>
    </>
  )
}

const HeaderText = styled(BoldText)`
  font-size: 54px;
  line-height: 70px;
  @media ${(p) => p.theme.deviceSize.mobile} {
    font-size: 30px;
    line-height: 44px;
  }
`

const ResponsiveRow = styled(Row)`
  @media ${(p) => p.theme.deviceSize.mobile} {
    flex-direction: column;
    align-items: flex-start;
  }
`

const DropdownSectionWrapper = styled(Column)`
  div:not(:last-child) {
    border-bottom: 0;
  }
`

const Table = styled(Column)`
  border-radius: 32px;
  border: 1px solid ${(p) => p.theme.color.black1};
  background-color: ${(p) => p.theme.color.white1};
  overflow: hidden;
`

const TableRow = styled(Row)`
  :not(:last-child) {
    border-bottom: 1px solid ${(p) => p.theme.color.black1};
  }
  div:first-child {
    border-right: 1px solid ${(p) => p.theme.color.black1};
  }
`

const TableItem = styled(Row)`
  flex: 1;
  width: 320px;
  height: 100px;
  justify-content: center;
  @media ${(p) => p.theme.deviceSize.mobile} {
    width: 100%;
    height: 80px;
  }
`

const TableHeaderText = styled(BoldText)`
  font-size: 25px;
  @media ${(p) => p.theme.deviceSize.mobile} {
    font-size: 15px;
  }
`

const TableRowText = styled(BoldText)`
  font-size: 20px;
  @media ${(p) => p.theme.deviceSize.mobile} {
    font-size: 13px;
  }
`

const Section = styled(Column)`
  input[type='number']::-webkit-outer-spin-button,
  input[type='number']::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
  input::-webkit-outer-spin-button,
  input::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  /* Firefox */
  input[type='number'] {
    -moz-appearance: textfield;
  }
  padding: 150px 0;
  @media ${(p) => p.theme.deviceSize.mobile} {
    padding: 40px 0;
    a {
      width: 100%;
    }
  }
`

const SubText = styled(H4Text)`
  line-height: 1.6;
  font-weight: ${(p) => p.theme.fontWeight.semiBold};
  color: ${(p) => p.theme.color.black2};
  @media ${(p) => p.theme.deviceSize.mobile} {
    font-weight: ${(p) => p.theme.fontWeight.regular};
    font-size: ${(p) => p.theme.fontSize.desc};
  }
`

const HireWrapper = styled(Row)`
  gap: 2px;
  & :first-child {
    border-radius: 10px 0 0 10px;
  }
  & :last-child {
    border-radius: 0 10px 10px 0;
  }
`

const Container = styled(Row)<{ isOpen: boolean; isFullWidth: boolean }>`
  gap: 24px;
  width: ${(p) => (p.isFullWidth ? '100%' : '510px')};
  height: ${(p) => (p.isOpen ? 160 : 90)}px;
  justify-content: space-between;
  border-top: 1px solid #767779;
  border-bottom: 1px solid #767779;
  padding: 30px 0px;
  cursor: pointer;
  align-items: start;
  transition: 0.4s;
  @media ${(p) => p.theme.deviceSize.mobile} {
    width: 100%;
    height: ${(p) => (p.isOpen ? 240 : 90)}px;
    align-items: center;
  }
`

const AccordionSubText = styled(SemiBoldText)`
  font-size: 24px;
  color: ${(p) => p.theme.color.black1};
  @media ${(p) => p.theme.deviceSize.mobile} {
    font-size: ${(p) => p.theme.fontSize.desc};
  }
`

const TableGrid = styled.div`
  display: grid;
  grid-template-columns: 200px 20px 180px 44px 240px;
  justify-items: center;
  align-items: center;
`
